// Map registry (sim side, pure). Every folder src/maps/<id>/ with a core.ts exporting `MAP: MapCoreDef` is picked up
// automatically — adding a map never touches this file. Order and unlock chain come from each def (`order`, `unlock`).
// The render half is discovered the same way by src/maps/renderRegistry.ts. Pure: no three / DOM (check:ip enforces it).
import type { MapCoreDef, MapProgress, TodChoice } from '../core/mapdef';
import { isUnlocked, TOD_CHOICES } from '../core/mapdef';

const found = import.meta.glob<{ MAP: MapCoreDef }>('./*/core.ts', { eager: true });

/** All maps, by select-page order. */
export const MAPS: readonly MapCoreDef[] = Object.entries(found)
  .map(([path, m]) => {
    const def = m.MAP, folder = path.split('/')[1];
    if (!def || def.id !== folder) throw new Error(`map folder ${folder}: core.ts must export MAP with id '${folder}'`);
    return def;
  })
  .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));

/** Map #1: always open, used when nothing (or something unknown) is asked for. */
export const DEFAULT_MAP = 'canyon-meadow';

export function mapById(id: string | null | undefined): MapCoreDef {
  return MAPS.find((m) => m.id === id) ?? MAPS.find((m) => m.id === DEFAULT_MAP) ?? MAPS[0];
}
export const isTodChoice = (s: unknown): s is TodChoice => typeof s === 'string' && (TOD_CHOICES as readonly string[]).includes(s);

/** Ids the player may pick: unlocked by progress AND ready ('wip' maps only with the dev override). */
export function playableMaps(progress: MapProgress, all = false): Set<string> {
  const out = new Set<string>();
  for (const m of MAPS) if (all || (m.status === 'ready' && isUnlocked(m, progress))) out.add(m.id);
  return out;
}
/** Maps that a new best time just opened (for the 'NEW ARENA UNLOCKED' toast): unlocked now, not before. */
export function newlyUnlocked(before: MapProgress, after: MapProgress): MapCoreDef[] {
  return MAPS.filter((m) => !isUnlocked(m, before) && isUnlocked(m, after));
}
